import { Card, CardContent } from '@/components/ui/card';
import { Clock, DollarSign, TrendingUp } from 'lucide-react';

interface TimeSummaryProps {
  logs: Array<{
    duration_minutes: number;
    is_billable: boolean;
    hourly_rate?: number;
  }>;
}

const TimeSummary = ({ logs }: TimeSummaryProps) => {
  const totalMinutes = logs.reduce((sum, log) => sum + log.duration_minutes, 0);
  const totalHours = (totalMinutes / 60).toFixed(2);

  const billableLogs = logs.filter((log) => log.is_billable);
  const billableMinutes = billableLogs.reduce((sum, log) => sum + log.duration_minutes, 0);
  const billableHours = (billableMinutes / 60).toFixed(2);

  const billableAmount = billableLogs.reduce((sum, log) => {
    if (log.hourly_rate) {
      return sum + (log.duration_minutes / 60) * log.hourly_rate;
    }
    return sum;
  }, 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Total Hours */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Hours</p>
              <p className="text-2xl font-bold mt-1">{totalHours}h</p>
              <p className="text-xs text-muted-foreground mt-1">
                {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
              </p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Clock className="h-6 w-6 text-blue-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billable Hours */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Billable Hours</p>
              <p className="text-2xl font-bold mt-1">{billableHours}h</p>
              <p className="text-xs text-muted-foreground mt-1">
                {billableLogs.length} billable {billableLogs.length === 1 ? 'entry' : 'entries'}
              </p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billable Amount */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Billable Amount</p>
              <p className="text-2xl font-bold mt-1">${billableAmount.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Based on hourly rates
              </p>
            </div>
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-500" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimeSummary;
